import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";
import { Trophy, Target, Flame, Zap, ArrowRight, TrendingUp } from "lucide-react";
import { useAuth } from "../auth";
import { api } from "../lib/api";

const Ticker = ({ matches }) => {
  const { t } = useI18n();
  if (!matches.length) return null;
  const items = [...matches, ...matches];
  return (
    <div className="relative overflow-hidden border-y border-white/10 bg-black/40" data-testid="bet-ticker">
      <div className="ticker flex items-center gap-10 py-3 whitespace-nowrap w-max">
        {items.map((m, i) => (
          <div key={`${m.id}-${i}`} className="flex items-center gap-3 text-xs font-mono">
            <span className="text-[#00D37F] font-bold">{t("filter_live").toUpperCase()}</span>
            <span className="text-white/80">
              {m.home_flag} {m.home_team}
            </span>
            <span className="text-white/30">vs</span>
            <span className="text-white/80">
              {m.away_team} {m.away_flag}
            </span>
            <span className="text-[#FFB703] font-bold">{t("bet_pills_exact")}</span>
            <span className="text-white/30">|</span>
            <span className="text-[#00D37F] font-bold">{t("bet_pills_result")}</span>
            <span className="w-1 h-1 rounded-full bg-white/20" />
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Landing() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [stats, setStats] = useState({ players: 0, matches: 0 });

  useEffect(() => {
    api.get("/matches").then((r) => {
      setMatches(r.data.slice(0, 6));
      setStats((s) => ({ ...s, matches: r.data.length }));
    });
    api.get("/leaderboard").then((r) => setStats((s) => ({ ...s, players: r.data.length }))).catch(() => {});
  }, []);

  return (
    <div className="relative" data-testid="landing-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1508088062105-17d61307629d?auto=format&fit=crop&w=1920&q=70')",
          }}
        />
        <div className="absolute inset-0 pitch-pattern opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/70 to-[#0a0a0a]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-24">
          <div className="flex flex-wrap items-center gap-3 mb-8">
            <span className="inline-flex items-center gap-2 border border-[#00D37F]/30 bg-[#00D37F]/10 px-3 py-1">
              <span className="live-dot" style={{ background: "#00D37F", boxShadow: "0 0 10px #00D37F" }} />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] font-bold text-[#00D37F]">
                {t("open_pool")}
              </span>
            </span>
            <span className="inline-flex items-center gap-2 border border-white/15 bg-white/5 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">
              USA · MEX · CAN · 2026
            </span>
          </div>

          <h1
            className="font-display text-[56px] sm:text-[96px] lg:text-[140px] leading-[0.88] tracking-tight max-w-5xl"
            data-testid="hero-title"
          >
            {t("play_the")}
            <br />
            <span className="text-[#00D37F]">{t("world_cup")}</span>
            <br />
            {t("own_the_slip")}
          </h1>
          <p className="mt-6 max-w-xl text-base sm:text-lg text-white/70 leading-relaxed">
            {t("subtagline")} —{" "}
            <span className="text-[#00D37F] font-bold font-mono">{t("bet_pills_exact")}</span> ·{" "}
            <span className="text-[#FFB703] font-bold font-mono">{t("bet_pills_result")}</span>
          </p>

          {/* Stat row */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 max-w-3xl" data-testid="hero-stats">
            {[
              { k: "64", l: t("matches_short"), icon: Target, color: "#00D37F" },
              { k: "+1253", l: t("players_short"), icon: Flame, color: "#FFB703" },
              { k: "12", l: t("groups_short"), icon: Trophy, color: "#007AFF" },
              { k: "72", l: t("group_stage_fixtures"), icon: Zap, color: "#FF2D45" },
            ].map((s, i) => (
              <div key={i} className="bg-[#0a0a0a] p-4 flex items-center gap-3">
                <s.icon className="w-4 h-4" style={{ color: s.color }} />
                <div>
                  <div className="font-display text-3xl leading-none" style={{ color: s.color }}>
                    {s.k}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">
                    {s.l}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            {user ? (
              <Link
                to="/matches"
                data-testid="hero-view-matches"
                className="inline-flex items-center gap-2 h-14 px-8 bg-[#00D37F] hover:bg-[#00E78B] text-black font-head text-base transition-colors"
              >
                {t("view_matches")} <ArrowRight className="w-5 h-5" />
              </Link>
            ) : (
              <>
                <Link
                  to="/register"
                  data-testid="hero-register"
                  className="inline-flex items-center gap-2 h-14 px-8 bg-[#00D37F] hover:bg-[#00E78B] text-black font-head text-base transition-colors"
                >
                  {t("hero_cta")} <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/leaderboard"
                  data-testid="hero-leaderboard"
                  className="inline-flex items-center h-14 px-6 border border-white/30 hover:border-[#00D37F] hover:text-[#00D37F] text-white font-head text-base transition-colors"
                >
                  <TrendingUp className="w-5 h-5 mr-2" /> {t("nav_leaderboard")}
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      <Ticker matches={matches} />

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-[11px] uppercase tracking-[0.3em] font-mono text-[#00D37F] mb-3">
          {t("how_it_works").toUpperCase()}
        </div>
        <h2 className="font-display text-4xl sm:text-6xl mb-10 max-w-3xl">{t("how_subtitle")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {[
            {
              icon: Target,
              color: "#00D37F",
              title: t("feat1_title"),
              body: t("feat1_body"),
              stake: t("feat1_stake"),
            },
            {
              icon: Flame,
              color: "#FFB703",
              title: t("feat2_title"),
              body: t("feat2_body"),
              stake: t("feat2_stake"),
            },
            {
              icon: Trophy,
              color: "#FF2D45",
              title: t("feat3_title"),
              body: t("feat3_body"),
              stake: t("feat3_stake"),
            },
          ].map((f, i) => (
            <div
              key={i}
              className="border border-white/10 bg-[#0d0d0d] p-8 hover:border-[#00D37F]/40 transition-colors group"
              data-testid={`feature-card-${i}`}
            >
              <f.icon className="w-7 h-7" style={{ color: f.color }} />
              <h3 className="font-display text-3xl mt-6">{f.title}</h3>
              <p className="text-sm text-white/60 mt-3 leading-relaxed">{f.body}</p>
              <div
                className="inline-block mt-6 px-3 py-1 font-mono text-xs font-bold"
                style={{
                  color: f.color,
                  background: `${f.color}20`,
                  border: `1px solid ${f.color}40`,
                }}
              >
                {f.stake}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
